import React from 'react';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const stages = [
  {
    title: 'Signals',
    description: 'Small indicators of change observed across markets, technology, policy, and infrastructure.',
  },
  {
    title: 'Patterns',
    description: 'When signals repeat across time or sectors, they begin to form observable patterns.',
  },
  {
    title: 'Scenarios',
    description: 'Patterns strengthen or weaken scenarios that describe structural pressures moving through the system.',
  },
  {
    title: 'Decisions',
    description: 'Decision-makers use scenario movement to stay oriented and act before changes become obvious.',
  },
];

const PatternSignalExplainer: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <section className="my-12" aria-label="Pattern and Signal framework explainer">
      <h3 className="text-2xl font-bold mt-8 mb-2" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        Understanding the Pattern &amp; Signal Framework
      </h3>
      <p className="text-muted-foreground mb-8 text-base">
        Pattern &amp; Signal follows a simple principle: small signals accumulate into patterns, patterns shape scenarios, and scenarios inform better decisions.
      </p>

      <div
        className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-stretch gap-0`}
        role="list"
        aria-label="Framework stages"
      >
        {stages.map((stage, i) => (
          <React.Fragment key={stage.title}>
            {/* Card */}
            <div
              role="listitem"
              className="flex-1 border border-border rounded-md p-5 bg-card flex flex-col justify-start"
              style={{ minWidth: 0 }}
            >
              <span
                className="text-xs tracking-widest uppercase text-muted-foreground mb-2 block"
                style={{ fontFamily: '"Courier New", Courier, monospace' }}
              >
                Stage {i + 1}
              </span>
              <h4
                className="text-lg font-semibold mb-2 text-foreground"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                {stage.title}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {stage.description}
              </p>
            </div>

            {/* Arrow between cards */}
            {i < stages.length - 1 && (
              <div className={`flex items-center justify-center ${isMobile ? 'py-2' : 'px-2'}`} aria-hidden="true">
                {isMobile ? (
                  <ArrowDown className="h-5 w-5 text-muted-foreground/60" />
                ) : (
                  <ArrowRight className="h-5 w-5 text-muted-foreground/60" />
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-6 italic text-center">
        Pattern &amp; Signal tracks signals continuously, allowing patterns and scenarios to evolve across monitoring cycles.
      </p>
    </section>
  );
};

export default PatternSignalExplainer;
